import { adminCaptureException, apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

const SUBMIT_ORDER_URL = 'backoffice/order/submit';
const VALIDATE_VOUCHERS_URL = 'backoffice/order';

type SubmitOrderPayload = {
  id: string;
};

type SubmitOrderResponse = {
  success?: boolean;
  message?: string;
};

type ValidateVouchersResponse = {
  results?: Array<{
    code: string;
    eligible: boolean;
    reasons: string[];
    reward?: unknown;
  }>;
};

type LoadingStep = 'idle' | 'submitting' | 'validating';
type ApiErrorPayload = {
  message?: string;
  statusCode?: number;
  [key: string]: unknown;
};

const submitOrderFetcher = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<SubmitOrderResponse> => {
  const response = await apiInstance(token).patch<SubmitOrderResponse>(
    `${SUBMIT_ORDER_URL}/${payload.id}`,
    JSON.stringify({ id: payload.id })
  );
  return response.data;
};

const validateVouchersFetcher = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<ValidateVouchersResponse> => {
  const response = await apiInstance(token).post<ValidateVouchersResponse>(
    `${VALIDATE_VOUCHERS_URL}/${payload.id}/validate/vouchers`
  );
  return response.data;
};

export const useSubmitOrderWithValidation = () => {
  const session = useAuthStore();
  const token = session.access_token;
  const queryClient = useQueryClient();

  const submitOrderMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) =>
      submitOrderFetcher(token, payload),
  });

  const validateVouchersMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) =>
      validateVouchersFetcher(token, payload),
  });

  const submitOrderWithValidation = async (
    payload: SubmitOrderPayload
  ): Promise<void> => {
    await submitOrderMutation.mutateAsync(payload);
    try {
      await validateVouchersMutation.mutateAsync(payload);
    } catch (error: unknown) {
      // Complete error handling - handle both Axios and non-Axios errors
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal Validasi Voucher';

      if (isAxiosError<ApiErrorPayload>(error)) {
        // Handle Axios-specific errors
        const payloadData = error.response?.data;
        adminCaptureException(
          { message: payloadData?.message ?? error.message },
          (scope) => {
            scope.setTags({
              queryKey: 'submit-order-validate-vouchers',
              operation: 'validate_vouchers',
              status_code: payloadData?.statusCode,
            });
            scope.setUser({
              id: session.userId,
              email: session.userEmail,
            });
            scope.setContext('error', {
              error: payloadData ?? error.toJSON?.() ?? error.message,
              orderId: payload.id,
            });
            scope.setLevel('error');
          }
        );
      } else {
        // Handle non-Axios errors (network, JSON parse, runtime errors, etc.)
        adminCaptureException({ message: errorMessage }, (scope) => {
          scope.setTags({
            queryKey: 'submit-order-validate-vouchers',
            operation: 'validate_vouchers',
            error_type: 'non_axios',
          });
          scope.setUser({
            id: session.userId,
            email: session.userEmail,
          });
          scope.setContext('error', {
            error: errorMessage,
            orderId: payload.id,
          });
          scope.setLevel('error');
        });
      }
    }
    queryClient.invalidateQueries({ queryKey: [`Activities-${payload.id}`] });
    queryClient.invalidateQueries({ queryKey: ['Activities'] });
  };

  let loadingStep: LoadingStep = 'idle';
  if (submitOrderMutation.isPending) {
    loadingStep = 'submitting';
  } else if (validateVouchersMutation.isPending) {
    loadingStep = 'validating';
  }

  const isLoading = loadingStep !== 'idle';

  return {
    submitOrderWithValidation,
    loadingStep,
    isLoading,
    submitOrderMutation,
    validateVouchersMutation,
  };
};

export type UseSubmitOrderWithValidationReturn = ReturnType<
  typeof useSubmitOrderWithValidation
>;
